import { defineStore } from 'pinia'
import { api } from '../lib/api'
import { composeChallenge, sortChallenges } from '../lib/challenges'

export const useAppStore = defineStore('app', {
  state: () => ({
    session: null,
    profile: null,
    family: null,
    members: [],
    balances: [],
    categories: [],
    tasks: [],
    completions: [],
    withdrawals: [],
    challengeData: { challenges: [], tasks: [], participants: [], standings: [], prizes: [] },
    /** true til vi har sjekket om det finnes en aktiv session ved oppstart. */
    booting: true,
    loading: false,
    error: '',
    toast: null,
  }),

  getters: {
    isLoggedIn: (s) => Boolean(s.session),
    /** Innlogget, men ikke koblet til en familie ennå. */
    needsSetup: (s) => Boolean(s.session) && !s.profile,
    isParent: (s) => s.profile?.role === 'parent',
    isChild: (s) => s.profile?.role === 'child',

    starValueOre: (s) => s.family?.star_value_ore ?? 1000,
    currency: (s) => s.family?.currency ?? 'kr',

    children: (s) => s.members.filter((m) => m.role === 'child'),
    parents: (s) => s.members.filter((m) => m.role === 'parent'),

    balanceFor: (s) => (childId) =>
      s.balances.find((b) => b.child_id === childId) ?? { balance_ore: 0, total_stars: 0 },

    myBalance() {
      return this.balanceFor(this.profile?.id)
    },

    pending: (s) => s.completions.filter((c) => c.status === 'pending'),
    pendingWithdrawals: (s) => s.withdrawals.filter((w) => w.status === 'pending'),

    /** Badge-tallet på Oversikt: oppgaver og uttak som venter til sammen. */
    pendingCount() { return this.pending.length + this.pendingWithdrawals.length },

    childWithdrawalsEnabled: (s) => s.family?.child_withdrawals_enabled !== false,

    /**
     * Saldo minus det som ligger til godkjenning. Speiler
     * available_balance_ore i databasen, så barnet ser samme tall som
     * grensen faktisk regnes mot.
     */
    availableFor() {
      return (childId) => {
        const pending = this.withdrawals
          .filter((w) => w.child_id === childId && w.status === 'pending')
          .reduce((sum, w) => sum + Number(w.amount_ore), 0)
        return Number(this.balanceFor(childId).balance_ore) - pending
      }
    },

    myAvailable() {
      return this.availableFor(this.profile?.id)
    },

    categoryById: (s) => (id) => s.categories.find((c) => c.id === id) || null,
    memberById: (s) => (id) => s.members.find((m) => m.id === id) || null,

    activeTasks: (s) => s.tasks.filter((t) => t.active),

    /** Oppgaver et gitt barn kan velge blant. */
    tasksFor: (s) => (childId) =>
      s.tasks.filter((t) => t.active && (!t.assigned_to || t.assigned_to === childId)),

    challenges() {
      return sortChallenges(
        this.challengeData.challenges.map((c) =>
          composeChallenge(c, this.challengeData, this.memberById)))
    },

    activeChallenges() {
      return this.challenges.filter((c) => c.status === 'active')
    },

    /** Utfordringer et gitt barn deltar i. */
    challengesFor() {
      return (childId) =>
        this.challenges.filter((c) => c.participants.some((p) => p.child_id === childId))
    },
  },

  actions: {
    async init() {
      this.session = await api.getSession()

      // Et lagret token kan være gyldig signert selv om brukeren er borte.
      // Da er det bedre å logge ut med én gang enn å la brukeren møte
      // uforståelige databasefeil senere.
      if (this.session && !(await api.verifyUser())) {
        await api.signOut()
        this.session = null
        this.reset()
      }

      api.onAuthChange(async (session) => {
        const changed = session?.user?.id !== this.session?.user?.id
        this.session = session
        if (!session) this.reset()
        else if (changed) await this.loadAll()
      })
      if (this.session) await this.loadAll()
      this.booting = false
    },

    reset() {
      this.profile = null
      this.family = null
      this.members = []
      this.balances = []
      this.categories = []
      this.tasks = []
      this.completions = []
      this.withdrawals = []
      this.challengeData = { challenges: [], tasks: [], participants: [], standings: [], prizes: [] }
    },

    async loadAll() {
      this.loading = true
      this.error = ''
      try {
        this.profile = await api.loadProfile()
        if (!this.profile) return // venter på at familien opprettes

        // Faste lommepenger som har forfalt føres her, før saldoene hentes -
        // stacken har ingen planlegger som kan gjøre det om natta. Hvem som
        // helst i familien utløser det, også barnet, slik at pengene ligger
        // der uansett hvem som åpner appen først. Går det galt, skal resten av
        // appen laste som vanlig: en manglende utbetaling tas ved neste kall.
        try { await api.runDueAllowances() } catch { /* ikke kritisk */ }

        // Til statistikken: hvem var innom i dag. Skriver høyst én gang i
        // timen per person, og skal aldri kunne stoppe innlastingen.
        api.touchLastSeen().catch(() => {})

        const [family, members, balances, categories, tasks, completions, withdrawals, challengeData] =
          await Promise.all([
            api.loadFamily(),
            api.loadMembers(),
            api.loadBalances(),
            api.listCategories(),
            api.listTasks(),
            api.listCompletions({ limit: 200 }),
            api.listWithdrawals({ limit: 200 }),
            api.loadChallengeData(),
          ])
        this.family = family
        this.members = members
        this.balances = balances
        this.categories = categories
        this.tasks = tasks
        this.completions = completions
        this.withdrawals = withdrawals
        this.challengeData = challengeData
      } catch (err) {
        this.error = err.message
      } finally {
        this.loading = false
      }
    },

    /** Lettere oppfriskning etter en handling - henter det som endrer seg. */
    async refresh() {
      const [balances, completions, withdrawals, members, challengeData] = await Promise.all([
        api.loadBalances(),
        api.listCompletions({ limit: 200 }),
        api.listWithdrawals({ limit: 200 }),
        api.loadMembers(),
        api.loadChallengeData(),
      ])
      this.balances = balances
      this.completions = completions
      this.withdrawals = withdrawals
      this.members = members
      this.challengeData = challengeData
    },

    notify(message, kind = 'ok') {
      this.toast = { message, kind, id: Date.now() }
      setTimeout(() => {
        if (this.toast && Date.now() - this.toast.id >= 2800) this.toast = null
      }, 3000)
    },

    async signOut() {
      await api.signOut()
      this.session = null
      this.reset()
    },
  },
})
